import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import {
  BASE_TABS,
  CLIENT_DETAILS_TAB_ORDER_VERSION,
  resolveClientDetailsTabOrder,
} from '@/components/superclinic/client-details/constants';

interface ClientDetailsTabSettings {
  prep: boolean;
  visits: boolean;
  journey: boolean;
  appointments: boolean;
  consultations: boolean;
  clinical: boolean;
  internal_notes: boolean;
  clinical_notes: boolean;
  journal: boolean;
  offerings: boolean;
  addons: boolean;
  test_enrollments: boolean;
  marketplace: boolean;
  purchases: boolean;
  questionnaires: boolean;
  biomarker_analysis: boolean;
  biomarker_values: boolean;
  reports: boolean;
  fitness_assessment: boolean;
  videos: boolean;
  pmx_reports: boolean;
  sift: boolean;
  wearables: boolean;
  wearable_analysis: boolean;
  thrive_intelligence: boolean;
  genetic_features: boolean;
  roadmap: boolean;
  care_team: boolean;
  family: boolean;
  daily_log: boolean;
  chat: boolean;
  context: boolean;
  reminders: boolean;
  workbench: boolean;
}

interface ClientDiscoveryTabSettings {
  client_data: boolean;
  precheck: boolean;
  questionnaires: boolean;
  assessments: boolean;
  consultation: boolean;
  external_reports: boolean;
  internal_reports: boolean;
  biomarker_analysis: boolean;
  roadmap: boolean;
}

interface BiomarkerRangesColumnSettings {
  [key: string]: boolean;
}

interface CorporateDashboardTabSettings {
  journey: boolean;
  employees: boolean;
  analytics: boolean;
}

interface ViewSettings {
  // Navigation settings
  showAnalytics: boolean;
  showCorporateManagement: boolean;
  showBuilder: boolean;

  // Hub page settings
  hubTickets: boolean;
  hubAppointments: boolean;
  hubClients: boolean;

  // Client Details tab settings
  clientDetailsTabs: ClientDetailsTabSettings;
  clientDetailsTabOrder?: string[];
  clientDetailsTabOrderVersion?: number;

  // Client Discovery tab settings
  clientDiscoveryTabs: ClientDiscoveryTabSettings;
  clientDiscoveryTabOrder?: string[];

  // Biomarker Ranges table settings
  biomarkerRangesColumns?: BiomarkerRangesColumnSettings;

  // Corporate dashboard tab settings
  corporateDashboardTabs: CorporateDashboardTabSettings;

  // Debug panel setting
  debugPanel: boolean;
}

interface ViewSettingsContextType {
  viewSettings: ViewSettings;
  updateViewSetting: (key: keyof ViewSettings, value: boolean) => void;
  updateClientDetailsTabSetting: (tab: keyof ClientDetailsTabSettings, value: boolean) => void;
  updateClientDiscoveryTabSetting: (tab: keyof ClientDiscoveryTabSettings, value: boolean) => void;
  updateTabOrder: (
    key: 'clientDetailsTabOrder' | 'clientDiscoveryTabOrder',
    order: string[]
  ) => void;
  updateBiomarkerRangesColumns: (columns: BiomarkerRangesColumnSettings) => void;
  updateCorporateDashboardTabSetting: (
    tab: keyof CorporateDashboardTabSettings,
    value: boolean
  ) => void;
  resetViewSettings: () => void;
}

const defaultClientDetailsTabSettings: ClientDetailsTabSettings = {
  prep: true,
  visits: true,
  journey: true,
  appointments: true,
  consultations: true,
  clinical: true,
  internal_notes: true,
  clinical_notes: true,
  journal: true,
  offerings: true,
  addons: true,
  test_enrollments: true,
  marketplace: true,
  purchases: true,
  questionnaires: true,
  biomarker_analysis: true,
  biomarker_values: true,
  reports: true,
  fitness_assessment: true,
  videos: true,
  pmx_reports: true,
  sift: true,
  wearables: true,
  wearable_analysis: true,
  thrive_intelligence: true,
  genetic_features: true,
  roadmap: true,
  care_team: true,
  family: true,
  daily_log: true,
  chat: true,
  context: true,
  reminders: true,
  workbench: true,
};

const defaultClientDiscoveryTabSettings: ClientDiscoveryTabSettings = {
  client_data: true,
  precheck: true,
  questionnaires: true,
  assessments: true,
  consultation: true,
  external_reports: true,
  internal_reports: true,
  biomarker_analysis: true,
  roadmap: true,
};

const defaultCorporateDashboardTabSettings: CorporateDashboardTabSettings = {
  journey: true,
  employees: true,
  analytics: true,
};

const defaultViewSettings: ViewSettings = {
  showAnalytics: true,
  showCorporateManagement: true,
  showBuilder: true,
  hubTickets: true,
  hubAppointments: true,
  hubClients: true,
  clientDetailsTabs: { ...defaultClientDetailsTabSettings },
  clientDetailsTabOrder: [...BASE_TABS],
  clientDetailsTabOrderVersion: CLIENT_DETAILS_TAB_ORDER_VERSION,
  clientDiscoveryTabs: { ...defaultClientDiscoveryTabSettings },
  clientDiscoveryTabOrder: [
    'client_data',
    'precheck',
    'questionnaires',
    'assessments',
    'consultation',
    'external_reports',
    'internal_reports',
    'biomarker_analysis',
    'roadmap',
  ],
  corporateDashboardTabs: { ...defaultCorporateDashboardTabSettings },
  debugPanel: false,
};

const ViewSettingsContext = createContext<ViewSettingsContextType | undefined>(undefined);

export const ViewSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [viewSettings, setViewSettings] = useState<ViewSettings>(() => {
    try {
      const saved = localStorage.getItem('app-view-settings');
      if (saved) {
        const parsedSettings = JSON.parse(saved);

        // Merge new default tabs with existing settings
        const mergedClientDetailsTabs = {
          ...defaultClientDetailsTabSettings,
          ...(parsedSettings.clientDetailsTabs || {}),
        };
        const mergedTabOrder = resolveClientDetailsTabOrder(
          parsedSettings.clientDetailsTabOrder,
          parsedSettings.clientDetailsTabOrderVersion,
          defaultViewSettings.clientDetailsTabOrder ?? BASE_TABS
        );

        const mergedCorporateDashboardTabs = {
          ...defaultCorporateDashboardTabSettings,
          ...(parsedSettings.corporateDashboardTabs || {}),
        };

        // Migrate legacy hubTodos -> hubTickets (default true if unset)
        const migratedHubTickets =
          typeof parsedSettings.hubTickets === 'boolean' ? parsedSettings.hubTickets : true;
        delete parsedSettings.hubTodos;

        return {
          ...defaultViewSettings,
          ...parsedSettings,
          hubTickets: migratedHubTickets,
          clientDetailsTabs: mergedClientDetailsTabs,
          clientDetailsTabOrder:
            mergedTabOrder.length > 0 ? mergedTabOrder : defaultViewSettings.clientDetailsTabOrder,
          clientDetailsTabOrderVersion: CLIENT_DETAILS_TAB_ORDER_VERSION,
          clientDiscoveryTabOrder: Array.isArray(parsedSettings.clientDiscoveryTabOrder)
            ? parsedSettings.clientDiscoveryTabOrder
            : defaultViewSettings.clientDiscoveryTabOrder,
          corporateDashboardTabs: mergedCorporateDashboardTabs,
        };
      }
    } catch (error) {
      console.error('Error parsing view settings from localStorage:', error);
    }
    return defaultViewSettings;
  });

  // Save to localStorage whenever settings change
  useEffect(() => {
    try {
      localStorage.setItem('app-view-settings', JSON.stringify(viewSettings));
    } catch (error) {
      console.error('Error saving view settings to localStorage:', error);
    }
  }, [viewSettings]);

  const updateViewSetting = useCallback((key: keyof ViewSettings, value: boolean) => {
    setViewSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateClientDetailsTabSetting = useCallback(
    (tab: keyof ClientDetailsTabSettings, value: boolean) => {
      setViewSettings((prev) => ({
        ...prev,
        clientDetailsTabs: {
          ...prev.clientDetailsTabs,
          [tab]: value,
        },
      }));
    },
    []
  );

  const updateClientDiscoveryTabSetting = useCallback(
    (tab: keyof ClientDiscoveryTabSettings, value: boolean) => {
      setViewSettings((prev) => ({
        ...prev,
        clientDiscoveryTabs: {
          ...prev.clientDiscoveryTabs,
          [tab]: value,
        },
      }));
    },
    []
  );

  const updateCorporateDashboardTabSetting = useCallback(
    (tab: keyof CorporateDashboardTabSettings, value: boolean) => {
      setViewSettings((prev) => ({
        ...prev,
        corporateDashboardTabs: {
          ...prev.corporateDashboardTabs,
          [tab]: value,
        },
      }));
    },
    []
  );

  const updateTabOrder = useCallback(
    (key: 'clientDetailsTabOrder' | 'clientDiscoveryTabOrder', order: string[]) => {
      setViewSettings((prev) => ({
        ...prev,
        [key]: order,
      }));
    },
    []
  );

  const updateBiomarkerRangesColumns = useCallback((columns: BiomarkerRangesColumnSettings) => {
    setViewSettings((prev) => ({
      ...prev,
      biomarkerRangesColumns: columns,
    }));
  }, []);

  const resetViewSettings = useCallback(() => {
    setViewSettings(defaultViewSettings);
  }, []);

  const contextValue = useMemo(
    () => ({
      viewSettings,
      updateViewSetting,
      updateClientDetailsTabSetting,
      updateClientDiscoveryTabSetting,
      updateCorporateDashboardTabSetting,
      updateTabOrder,
      updateBiomarkerRangesColumns,
      resetViewSettings,
    }),
    [
      viewSettings,
      updateViewSetting,
      updateClientDetailsTabSetting,
      updateClientDiscoveryTabSetting,
      updateCorporateDashboardTabSetting,
      updateTabOrder,
      updateBiomarkerRangesColumns,
      resetViewSettings,
    ]
  );

  return (
    <ViewSettingsContext.Provider value={contextValue}>{children}</ViewSettingsContext.Provider>
  );
};

export const useViewSettings = () => {
  const context = useContext(ViewSettingsContext);
  if (context === undefined) {
    throw new Error('useViewSettings must be used within a ViewSettingsProvider');
  }
  return context;
};
